'use client';

import type {ReactNode} from 'react';

import {Icon} from '@udemy-v2/icons';
import {useI18n} from '@udemy-v2/i18n';
import classNames from 'classnames';

import type {InstructorProfile} from '@/features/instructor-profile/lib/types';

import {formatCompactCount, formatCount, formatRating} from '../../lib/format-stats';
import styles from './profile-header.module.scss';

interface ProfileHeaderProps {
    /**
     * The instructor profile to render. Presentational only — this component
     * never fetches; the public profile page and (later) the instructor preview
     * tab both pass already-resolved data here.
     */
    profile: InstructorProfile;
    /**
     * Optional CTA slot rendered under the job-title description, supplied by
     * the caller wrapped in its own `<Suspense>` boundary so it streams
     * independently of the hero copy. The public page passes
     * `ProfileCommunityCta`, which renders "Open Community" for subscribed
     * viewers and "Join my community" for everyone else; it resolves to nothing
     * when there is no community, and on the preview tab.
     */
    communityCta?: ReactNode;
}

/**
 * Public profile hero band, rendered directly under the site header.
 *
 * Mirrors Figma node 2944:39933: learner/rating stats, the instructor's name,
 * and their job-title description, laid over the cover photo. The "Skills I
 * teach" section is a separate component (later task) and is intentionally not
 * rendered here.
 */
export function ProfileHeader({profile, communityCta}: ProfileHeaderProps) {
    const {gettext, ninterpolate, locale} = useI18n();

    const {
        name,
        surname,
        job_title: jobTitle,
        total_num_students,
        avg_rating,
        total_num_reviews,
        cover_photo_1440x487: cover1440,
        cover_photo_1280x433: cover1280,
        cover_photo_1080x365: cover1080,
        cover_photo_960x400: cover960,
        mobile_cover_photo_960x860: mobileCover,
        community_cover_text_color_preference: coverTextColorPreference,
    } = profile;

    const fullName = [name, surname].filter(Boolean).join(' ');

    return (
        <section
            className={classNames(styles['profile-header'], {
                [styles['dark-text']]: coverTextColorPreference === 'DARK',
            })}
        >
            {cover1440 && (
                <picture className={styles['cover']}>
                    {/*
                     * At the phone breakpoint (xs-max, 600px) we both swap to
                     * the portrait mobile cover and flip to the stacked layout
                     * (see profile-header.module.scss). Keep these two in sync.
                     */}
                    {mobileCover && (
                        <source media="(max-width: 600px)" srcSet={mobileCover} />
                    )}
                    {cover960 && <source media="(max-width: 960px)" srcSet={cover960} />}
                    {cover1080 && <source media="(max-width: 1080px)" srcSet={cover1080} />}
                    {cover1280 && <source media="(max-width: 1280px)" srcSet={cover1280} />}
                    {/* Decorative: the instructor's name conveys the meaning. */}
                    {/* The cover is the page's LCP element, so hint the browser to fetch it first. */}
                    <img src={cover1440} alt="" fetchPriority="high" />
                </picture>
            )}

            <div className={styles['content']}>
                <div className={styles['stats']}>
                    {typeof total_num_students === 'number' && (
                        <span className={styles['stat']}>
                            <span className={styles['stat-metric']}>
                                {/*
                                 * `inherit` so the icon follows the section's
                                 * text colour: white over the cover on desktop,
                                 * dark on the white mobile layout.
                                 */}
                                <Icon
                                    glyph="people"
                                    size="medium"
                                    color="inherit"
                                    label={false}
                                />
                                <span className={styles['stat-value']}>
                                    {formatCompactCount(total_num_students, locale)}
                                </span>
                            </span>
                            <span className={styles['stat-label']}>{gettext('Learners')}</span>
                        </span>
                    )}
                    {typeof avg_rating === 'number' && (
                        <span className={styles['stat']}>
                            <span className={styles['stat-metric']}>
                                <Icon
                                    glyph="rating-star"
                                    size="medium"
                                    color="inherit"
                                    label={false}
                                    className={styles['star']}
                                />
                                <span className={styles['stat-value']}>
                                    {formatRating(avg_rating, locale)}
                                </span>
                            </span>
                            {typeof total_num_reviews === 'number' && (
                                <a className={styles['ratings-link']} href="#ratings">
                                    {ninterpolate(
                                        '%(count)s rating',
                                        '%(count)s ratings',
                                        total_num_reviews,
                                        {count: formatCount(total_num_reviews, locale)},
                                    )}
                                </a>
                            )}
                        </span>
                    )}
                </div>

                <div className={styles['text-block']}>
                    <h1 className={styles['name']}>{fullName}</h1>
                    {jobTitle && <p className={styles['description']}>{jobTitle}</p>}
                </div>
                {communityCta}
            </div>
        </section>
    );
}
